/**
 * Internal holds for one migration session (GDN-4065): the held rows on the
 * loaded page, the held counts, and hold / release through the generated
 * routes. Returns the `internalHold` view model ImportReviewBrowser takes.
 *
 * The filter mode lives in the host, not here: the host's main browse needs it
 * as an argument before the rows this hook reads exist.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { HoldsResponse } from '@store/generated/admin';
import { useSnackbar } from 'notistack';

import type { ImportReviewBrowserProps } from '../components/import-review-browser';
import { reconcileHoldFilter } from '../components/internal-hold-copy';
import type {
  HoldAction,
  HoldActionResult,
  HoldFilterMode,
  InternalHold,
} from '../components/internal-hold-copy';
import type { BrowsedStagedResource } from '../store/api';
import {
  useBrowseStagedResourcesQuery,
  useHoldImportRowsMutation,
  useReleaseImportRowHoldsMutation,
} from '../store/hooks';
import { readLaunchRefusal } from '../utils/launch-refusal';

type HoldOutcome = HoldsResponse['outcomes'][number];

/** Outcomes that are not refusals: a change, or already in that state. */
const NOT_REFUSED = new Set<HoldOutcome['outcome']>([
  'held',
  'reason_updated',
  'released',
  'flagged',
  'unflagged',
  'unchanged',
]);

/** Whether a per-row outcome (hold, release or org review) is a refusal. */
export function isRefusedOutcome(outcome: HoldOutcome['outcome']): boolean {
  return !NOT_REFUSED.has(outcome);
}

/** The browse filter argument for a hold filter mode. */
export function holdFilterParam(mode: HoldFilterMode): boolean | undefined {
  return mode === 'held' ? true : mode === 'not_held' ? false : undefined;
}

/**
 * One row's hold, if held. The server falls back to the raw user id when a
 * name no longer resolves; an id is not a name, so it shows no name instead.
 */
export function readHold(row: BrowsedStagedResource): InternalHold | undefined {
  if (!row.internal_hold) return undefined;
  const name = row.internal_hold_by_name?.trim();
  return {
    reason: row.internal_hold_reason?.trim() ?? '',
    byName: name && name !== row.internal_hold_by ? name : null,
    at: row.internal_hold_at ?? null,
  };
}

/** The server's per-row result, as the browser's result panel reads it. */
export function toHoldResult(action: HoldAction, response: HoldsResponse): HoldActionResult {
  const outcomes = response.outcomes ?? [];
  const reasonUpdated = outcomes.filter(o => o.outcome === 'reason_updated').length;
  return {
    action,
    changed: response.changed - reasonUpdated,
    reasonUpdated,
    unchanged: response.unchanged,
    refused: outcomes
      .filter(o => isRefusedOutcome(o.outcome))
      .map(o => ({
        id: o.import_resource_id,
        // The server sets a message on every refusal; never drop a row that
        // somehow lacks one.
        message:
          o.message?.trim() || `Row ${o.import_resource_id}: ${o.outcome.replace(/_/g, ' ')}.`,
      })),
  };
}

export function useInternalHolds({
  organizationId,
  sessionId,
  rows,
  filterMode,
  setFilterMode,
  skip,
}: {
  organizationId: string;
  sessionId: string;
  /** The loaded page's rows. */
  rows: BrowsedStagedResource[] | undefined;
  filterMode: HoldFilterMode;
  setFilterMode: (mode: HoldFilterMode) => void;
  skip: boolean;
}): NonNullable<ImportReviewBrowserProps['internalHold']> {
  const { enqueueSnackbar } = useSnackbar();
  const [lastResult, setLastResult] = useState<HoldActionResult | null>(null);
  const [holdRows, { isLoading: holding }] = useHoldImportRowsMutation();
  const [releaseRows, { isLoading: releasing }] = useReleaseImportRowHoldsMutation();

  // Totals for the Held / Not held segments, from filtered browses: one row
  // each, since only `total` is read. The same pattern as the flag counts.
  const { data: heldPage } = useBrowseStagedResourcesQuery(
    { organizationId, sessionId, page: 1, perPage: 1, internalHold: true },
    { skip },
  );
  const { data: notHeldPage } = useBrowseStagedResourcesQuery(
    { organizationId, sessionId, page: 1, perPage: 1, internalHold: false },
    { skip },
  );
  const total = heldPage?.total ?? 0;
  const notHeldTotal = notHeldPage?.total ?? 0;

  useEffect(() => {
    const next = reconcileHoldFilter(filterMode, total, notHeldTotal);
    if (next !== filterMode) setFilterMode(next);
  }, [filterMode, total, notHeldTotal, setFilterMode]);

  const holds = useMemo(() => {
    const out = new Map<string, InternalHold>();
    for (const row of rows ?? []) {
      const hold = readHold(row);
      if (hold) out.set(row.import_resource_id, hold);
    }
    return out;
  }, [rows]);

  const run = useCallback(
    async (
      action: HoldAction,
      target: { ids: string[] } | { selection: true },
      reason?: string,
    ) => {
      // The panel reports THIS request; the last one's result would read as
      // this one's while it runs.
      setLastResult(null);
      const scope = 'ids' in target ? { import_resource_ids: target.ids } : { use_selection: true };
      try {
        const response =
          action === 'hold'
            ? await holdRows({
                sessionId,
                holdStagedResourcesRequest: {
                  organization_id: organizationId,
                  reason: reason ?? '',
                  ...scope,
                },
              }).unwrap()
            : await releaseRows({
                sessionId,
                releaseStagedResourceHoldsRequest: { organization_id: organizationId, ...scope },
              }).unwrap();
        setLastResult(toHoldResult(action, response));
      } catch (error) {
        // A refused ROW comes back in a 200's outcomes; reaching here means the
        // request itself failed. A timeout or a dropped connection says nothing
        // about whether the write landed, so never claim nothing changed — and
        // an unconfirmed hold is exactly what must be checked before a commit.
        const failure = readLaunchRefusal(error);
        const noun = action === 'hold' ? 'hold' : 'release';
        enqueueSnackbar(
          failure.unconfirmed
            ? `Could not confirm the ${noun}. Refresh to see which rows are held before committing.`
            : `The ${noun} was not accepted${
                failure.message
                  ? `: ${failure.message}`
                  : failure.status
                    ? ` (HTTP ${failure.status}).`
                    : '.'
              }`,
          { variant: 'error' },
        );
      }
    },
    [holdRows, releaseRows, organizationId, sessionId, enqueueSnackbar],
  );

  return {
    holds,
    onHold: (ids, reason) => void run('hold', { ids }, reason),
    onRelease: ids => void run('release', { ids }),
    onHoldSelection: reason => void run('hold', { selection: true }, reason),
    onReleaseSelection: () => void run('release', { selection: true }),
    total,
    notHeldTotal,
    filterMode,
    onFilterModeChange: setFilterMode,
    inFlight: holding || releasing,
    lastResult,
    onDismissResult: () => setLastResult(null),
  };
}
